import type { KeyboardEvent } from "react";
import { useState, useCallback, ChangeEvent, FormEvent } from "react";
import { AvailableLanguages, Country } from "utils/types";
import {
  bestCountryMatch,
  capitalizeFirstLetter,
  parsedCountryName,
} from "utils/functions";
import countriesData from "utils/countriesData.json";
import { useNavigate } from "react-router-dom";

type Suggestion = {
  country: Country;
  primaryLabel: string;
  secondaryLabel: string;
};

const getCountryLabel = (country: Country, lang?: AvailableLanguages) =>
  lang === AvailableLanguages.ES ? country["spanish-name"] : country["english-name"];

const buildSuggestions = (
  rawQuery: string,
  lang: AvailableLanguages | undefined
): Suggestion[] => {
  const q = parsedCountryName(rawQuery).trim();
  if (!q) return [];

  const otherLang =
    lang === AvailableLanguages.ES ? AvailableLanguages.EN : AvailableLanguages.ES;

  const scored = (countriesData as unknown as Country[])
    .map((country) => {
      const primary = getCountryLabel(country, lang);
      const secondary = getCountryLabel(country, otherLang);
      const p = parsedCountryName(primary);
      const s = parsedCountryName(secondary);

      const matchesPrimary = p.startsWith(q);
      const matchesSecondary = s.startsWith(q);
      if (!matchesPrimary && !matchesSecondary) return null;

      const score =
        (matchesPrimary ? 320 : 0) +
        (matchesSecondary ? 120 : 0) +
        Math.max(0, 40 - Math.max(0, p.length - q.length));

      return { country, primary, secondary, score };
    })
    .filter((x): x is NonNullable<typeof x> => !!x)
    .sort((a, b) => b.score - a.score || a.primary.length - b.primary.length);

  const dedup = new Map<string, Suggestion>();
  for (const r of scored) {
    if (!dedup.has(r.primary)) {
      dedup.set(r.primary, {
        country: r.country,
        primaryLabel: r.primary,
        secondaryLabel: r.secondary,
      });
    }
  }

  return Array.from(dedup.values());
};

export const useCountryForm = (
  language: AvailableLanguages | undefined,
  setMatchingPath: (path: string) => void
) => {
  const [typedValue, setTypedValue] = useState<
    Country["english-name"] | Country["spanish-name"] | string
  >("");
  const [inputValue, setInputValue] = useState<string>("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const userInput = capitalizeFirstLetter(e.target.value);
    setTypedValue(userInput);
    setInputValue(userInput);

    const nextSuggestions = buildSuggestions(userInput, language);
    setSuggestions(nextSuggestions);
    setIsOpen(nextSuggestions.length > 0);
    setActiveIndex(nextSuggestions.length === 1 ? 0 : -1);

    // Keep map highlighting responsive for short inputs (1–2 chars),
    // matching the previous behavior.
    setMatchingPath(userInput.trim().length > 0 ? parsedCountryName(userInput) : "");
  };

  const previewSuggestion = (index: number) => {
    if (index < 0 || index >= suggestions.length) return;
    setActiveIndex(index);
    // Preview should NOT autocomplete the input value; only highlight the map/result.
    const label = suggestions[index].primaryLabel;
    setMatchingPath(parsedCountryName(label));
    setIsOpen(true);
  };

  const closeSuggestions = () => {
    setIsOpen(false);
    setActiveIndex(-1);
    setSuggestions([]);
    setInputValue(typedValue);
    setMatchingPath(typedValue.trim().length > 0 ? parsedCountryName(typedValue) : "");
  };

  const resetPreview = () => {
    setInputValue(typedValue);
    setMatchingPath(typedValue.trim().length > 0 ? parsedCountryName(typedValue) : "");
  };

  const commitSuggestion = (index: number) => {
    if (index < 0 || index >= suggestions.length) return;
    const s = suggestions[index];

    setTypedValue(s.primaryLabel);
    setInputValue(s.primaryLabel);
    setIsOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);

    const canonicalName = getCountryLabel(s.country, language);
    navigate(`/countries/${encodeURIComponent(canonicalName)}`);
  };

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!typedValue) return;

      if (suggestions.length > 0) {
        if (suggestions.length > 1 && activeIndex < 0) {
          setIsOpen(true);
          return;
        }

        const idx =
          activeIndex >= 0 ? activeIndex : suggestions.length === 1 ? 0 : -1;
        if (idx < 0) return;
        const s = suggestions[idx];
        if (!s) return;

        setTypedValue(s.primaryLabel);
        setInputValue(s.primaryLabel);
        setIsOpen(false);
        setSuggestions([]);
        setActiveIndex(-1);

        const canonicalName = getCountryLabel(s.country, language);
        navigate(`/countries/${encodeURIComponent(canonicalName)}`);
        return;
      }

      const best = bestCountryMatch(countriesData as unknown as Country[], typedValue);
      if (!best) return;
      const canonicalName = getCountryLabel(best.country, language);
      navigate(`/countries/${encodeURIComponent(canonicalName)}`);
    },
    [typedValue, suggestions, activeIndex, navigate, language]
  );

  return {
    onSubmit,
    language,
    onChange,
    inputValue,
    typedValue,
    suggestions,
    activeIndex,
    isOpen,
    previewSuggestion,
    commitSuggestion,
    closeSuggestions,
    resetPreview,
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || suggestions.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next =
          activeIndex < 0
            ? 0
            : activeIndex < suggestions.length - 1
              ? activeIndex + 1
              : 0;
        previewSuggestion(next);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        const next =
          activeIndex < 0
            ? suggestions.length - 1
            : activeIndex > 0
              ? activeIndex - 1
              : suggestions.length - 1;
        previewSuggestion(next);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeSuggestions();
        return;
      }
    },
    onFocus: () => {
      if (suggestions.length > 0) setIsOpen(true);
    },
    onBlur: () => {
      window.setTimeout(() => {
        setInputValue(typedValue);
        setIsOpen(false);
        setMatchingPath(
          typedValue.trim().length > 0 ? parsedCountryName(typedValue) : ""
        );
      }, 120);
    },
  };
};
